import { useState, useEffect, useCallback } from 'react';
import { useSupplierAuth } from '../../context/SupplierAuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  Inbox, 
  Search,
  Filter,
  Clock,
  Building2,
  Mail,
  Phone,
  ChevronRight,
  Check,
  X,
  AlertCircle,
  MessageSquare
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { getUserErrorMessage } from '../../utils/errorMessages';

const statusLabels = {
  'nieuw': { label: 'Nieuw', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  'contact_gelegd': { label: 'Contact gelegd', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  'voorstel_gedaan': { label: 'Voorstel gedaan', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  'bezig': { label: 'Bezig', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  'afgerond': { label: 'Afgerond', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  'gestopt': { label: 'Gestopt', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

const helpTypeLabels = {
  'implementatiehulp': 'Implementatie',
  'advies': 'Advies',
  'toolingselectie': 'Tooling'
};

const SupplierLeadsPage = () => {
  const { api } = useSupplierAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusCounts, setStatusCounts] = useState({});
  
  // Detail dialog
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  
  // Accept/Decline dialogs
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  const fetchLeads = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await api.get('/leads', { params });
      setLeads(response.data.leads);
      setStatusCounts(response.data.status_counts);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      toast.error('Kon leads niet laden');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const filteredLeads = leads.filter(lead => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      lead.shared_data?.company_name?.toLowerCase().includes(query) ||
      lead.summary?.toLowerCase().includes(query) ||
      lead.help_type?.toLowerCase().includes(query)
    );
  });

  const handleViewDetail = async (lead) => {
    try {
      const response = await api.get(`/leads/${lead.id}`);
      setSelectedLead(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Kon lead details niet laden');
    }
  };

  const handleAcceptLead = async () => {
    if (!selectedLead) return;
    setProcessingAction(true);
    
    try {
      const response = await api.post(`/leads/${selectedLead.lead.id}/respond`, { action: 'accept' });
      
      if (response.data.warning) {
        // Duplicate warning
        toast.warning(response.data.warning);
        // Show confirm dialog for duplicate
        if (window.confirm(response.data.message)) {
          await api.post(`/leads/${selectedLead.lead.id}/confirm-accept`);
          toast.success('Lead geaccepteerd');
        }
      } else {
        toast.success(`Lead geaccepteerd. Lead fee: €${response.data.lead_fee}`);
      }
      
      setAcceptDialogOpen(false);
      setDetailDialogOpen(false);
      fetchLeads();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Kon lead niet accepteren'));
    } finally {
      setProcessingAction(false);
    }
  };

  const handleDeclineLead = async () => {
    if (!selectedLead || !declineReason) return;
    setProcessingAction(true);
    
    try {
      await api.post(`/leads/${selectedLead.lead.id}/respond`, { 
        action: 'decline',
        decline_reason: declineReason
      });
      toast.success('Lead afgewezen');
      setDeclineDialogOpen(false);
      setDetailDialogOpen(false);
      setDeclineReason('');
      fetchLeads();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Kon lead niet afwijzen'));
    } finally {
      setProcessingAction(false);
    }
  };

  const handleUpdateStatus = async (leadId, newStatus) => {
    try {
      await api.patch(`/leads/${leadId}/status`, { status: newStatus });
      toast.success('Status bijgewerkt');
      fetchLeads();
      if (selectedLead) {
        const response = await api.get(`/leads/${leadId}`);
        setSelectedLead(response.data);
      }
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Kon status niet bijwerken'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black dark:border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
            Lead Inbox
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            Beheer uw aanvragen van potentiële klanten
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">
            <Inbox className="h-3 w-3 mr-1" />
            {statusCounts.nieuw || 0} nieuw
          </Badge>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <Input
            placeholder="Zoeken op bedrijfsnaam of type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter op status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            {Object.entries(statusLabels).map(([key, { label }]) => (
              <SelectItem key={key} value={key}>
                {label} ({statusCounts[key] || 0})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Leads List */}
      {filteredLeads.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-neutral-500 dark:text-neutral-400">
              <Inbox className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">Geen leads gevonden</p>
              <p className="text-sm mt-1">
                {statusFilter !== 'all' 
                  ? 'Probeer een andere filter' 
                  : 'Nieuwe leads verschijnen hier wanneer klanten contact met u opnemen'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredLeads.map((lead) => (
            <Card 
              key={lead.id}
              className="cursor-pointer hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors"
              onClick={() => handleViewDetail(lead)}
              data-testid={`lead-card-${lead.id}`}
            >
              <CardContent className="py-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Building2 className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                      <span className="font-medium text-neutral-900 dark:text-white truncate">
                        {lead.shared_data?.company_name || 'Bedrijf'}
                      </span>
                      <Badge className={statusLabels[lead.status]?.color || ''}>
                        {statusLabels[lead.status]?.label || lead.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 truncate mb-2">
                      {lead.summary}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-neutral-500 dark:text-neutral-400">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(lead.created_at).toLocaleDateString('nl-NL')}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {helpTypeLabels[lead.help_type] || lead.help_type}
                      </Badge>
                      {lead.urgency === 'hoog' && (
                        <Badge variant="destructive" className="text-xs">Urgent</Badge>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-neutral-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Lead Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedLead && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  {selectedLead.lead.shared_data?.company_name || 'Bedrijf'}
                </DialogTitle>
                <DialogDescription>
                  Lead ontvangen op {new Date(selectedLead.lead.created_at).toLocaleDateString('nl-NL')}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Status */}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">Status:</span>
                  <Badge className={statusLabels[selectedLead.lead.status]?.color || ''}>
                    {statusLabels[selectedLead.lead.status]?.label || selectedLead.lead.status}
                  </Badge>
                  {selectedLead.lead.lead_status === 'accepted' && (
                    <Select 
                      value={selectedLead.lead.status}
                      onValueChange={(value) => handleUpdateStatus(selectedLead.lead.id, value)}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="contact_gelegd">Contact gelegd</SelectItem>
                        <SelectItem value="voorstel_gedaan">Voorstel gedaan</SelectItem>
                        <SelectItem value="bezig">Bezig</SelectItem>
                        <SelectItem value="afgerond">Afgerond</SelectItem>
                        <SelectItem value="gestopt">Gestopt</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Request Details */}
                <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-lg p-4 space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">Type hulp</h4>
                    <p className="text-neutral-900 dark:text-white">
                      {helpTypeLabels[selectedLead.lead.help_type] || selectedLead.lead.help_type}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">Hulpvraag</h4>
                    <p className="text-neutral-900 dark:text-white">{selectedLead.lead.summary}</p>
                  </div>
                  {selectedLead.lead.desired_outcome && (
                    <div>
                      <h4 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">Gewenst resultaat</h4>
                      <p className="text-neutral-900 dark:text-white">{selectedLead.lead.desired_outcome}</p>
                    </div>
                  )}
                  <div className="flex items-center gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">Urgentie</h4>
                      <Badge variant={selectedLead.lead.urgency === 'hoog' ? 'destructive' : 'outline'}>
                        {selectedLead.lead.urgency}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">Contactgegevens</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 className="h-4 w-4 text-neutral-400" />
                      <span className="text-neutral-900 dark:text-white">
                        {selectedLead.lead.contact?.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-neutral-400" />
                      <a 
                        href={`mailto:${selectedLead.lead.contact?.email}`}
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {selectedLead.lead.contact?.email}
                      </a>
                    </div>
                    {selectedLead.lead.contact?.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-neutral-400" />
                        <a 
                          href={`tel:${selectedLead.lead.contact?.phone}`}
                          className="text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {selectedLead.lead.contact?.phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Shared Data Info */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">Gedeelde informatie</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {selectedLead.lead.consent_given?.company_size && (
                      <div>
                        <span className="text-blue-600 dark:text-blue-400">Bedrijfsgrootte:</span>{' '}
                        <span className="text-neutral-900 dark:text-white">
                          {selectedLead.lead.shared_data?.company_size || 'Niet gespecificeerd'}
                        </span>
                      </div>
                    )}
                    {selectedLead.lead.consent_given?.sector && (
                      <div>
                        <span className="text-blue-600 dark:text-blue-400">Sector:</span>{' '}
                        <span className="text-neutral-900 dark:text-white">
                          {selectedLead.lead.shared_data?.sector || 'Niet gespecificeerd'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status History */}
                {selectedLead.status_history?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">Geschiedenis</h4>
                    <div className="space-y-2">
                      {selectedLead.status_history.slice(0, 5).map((item) => (
                        <div key={`${item.timestamp}-${item.action}`} className="flex items-center gap-2 text-sm">
                          <div className="h-2 w-2 rounded-full bg-neutral-300 dark:bg-neutral-600" />
                          <span className="text-neutral-600 dark:text-neutral-400">
                            {new Date(item.timestamp).toLocaleString('nl-NL')}
                          </span>
                          <span className="text-neutral-900 dark:text-white">
                            {item.action.replace(/_/g, ' ')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                {selectedLead.lead.status === 'nieuw' && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => setDeclineDialogOpen(true)}
                      className="text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/20"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Afwijzen
                    </Button>
                    <Button onClick={() => setAcceptDialogOpen(true)}>
                      <Check className="h-4 w-4 mr-2" />
                      Accepteren
                    </Button>
                  </>
                )}
                {selectedLead.lead.lead_status === 'accepted' && (
                  <Button
                    variant="default"
                    onClick={() => window.location.href = `mailto:${selectedLead.lead.contact?.email}`}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Contact opnemen
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Accept Confirmation Dialog */}
      <Dialog open={acceptDialogOpen} onOpenChange={setAcceptDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lead accepteren</DialogTitle>
            <DialogDescription>
              Door deze lead te accepteren bevestigt u dat deze billable wordt volgens de Lead Fee Policy. 
              U heeft 14 dagen om te disputen.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-800 dark:text-yellow-200">Lead fee van toepassing</p>
                  <p className="text-yellow-700 dark:text-yellow-300 mt-1">
                    Bij acceptatie wordt een lead fee in rekening gebracht volgens de actuele policy.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptDialogOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleAcceptLead} disabled={processingAction}>
              {processingAction ? 'Verwerken...' : 'Accepteren'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline Dialog */}
      <Dialog open={declineDialogOpen} onOpenChange={setDeclineDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lead afwijzen</DialogTitle>
            <DialogDescription>
              Geef een reden op voor het afwijzen van deze lead.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={declineReason} onValueChange={setDeclineReason}>
              <SelectTrigger>
                <SelectValue placeholder="Selecteer een reden" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="geen_capaciteit">Geen capaciteit</SelectItem>
                <SelectItem value="out_of_scope">Buiten scope</SelectItem>
                <SelectItem value="regio_niet">Regio niet ondersteund</SelectItem>
                <SelectItem value="te_weinig_info">Te weinig informatie</SelectItem>
                <SelectItem value="conflict">Conflict of interest</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setDeclineDialogOpen(false);
              setDeclineReason('');
            }}>
              Annuleren
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeclineLead}
              disabled={!declineReason || processingAction}
            >
              {processingAction ? 'Verwerken...' : 'Afwijzen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierLeadsPage;
